<template>
    <div
        class="ppv2-flex ppv2-flex-wrap ppv2-w-full ppv2-step-wrapper md:ppv2-gap-[3.125vw]"
        :style="{ paddingLeft: this.normalizeSize(55) }"
    >
        <div class="form-section">
            <HeaderStep :step="step" :sizes="sizes" />

            <!-- 🔥 BADGE DE DÉTECTION DU TYPE -->
            <div v-if="detectedType && showDetectedType" class="detection-badge-container ppv2-mb-4">
                <div
                    class="detection-badge ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-px-3 ppv2-py-1 ppv2-rounded-lg ppv2-border ppv2-mb-7"
                    :class="[
                        hasDetectionAmbiguity
                            ? 'ppv2-bg-orange-50 ppv2-border-orange-400'
                            : 'ppv2-bg-[#31d0aa] ppv2-bg-opacity-10 ppv2-border-[#31d0aa]',
                        'dark:ppv2-bg-opacity-10',
                    ]"
                >
                    <span class="ppv2-text-xl" :class="hasDetectionAmbiguity ? 'ppv2-text-orange-600' : 'ppv2-text-[#31d0aa]'">
                        {{ hasDetectionAmbiguity ? '⚠️' : '✓' }}
                    </span>
                    <div class="ppv2-flex-1">
                        <div
                            class="poppinsMedium ppv2-text-sm"
                            :class="hasDetectionAmbiguity ? 'ppv2-text-orange-800' : 'ppv2-text-[#31d0aa]'"
                        >
                            {{ detectionMessage }}
                        </div>
                        <div v-if="hasDetectionAmbiguity" class="ppv2-text-xs ppv2-text-orange-600 ppv2-mt-1">
                            {{ $t('experience.step3.detection.ambiguityHint') }}
                        </div>
                    </div>
                    <div class="ppv2-text-xl poppinsBold ppv2-text-black ppv2-text-opacity-70">
                        {{ Math.round(detectionInfo.score * 100) }}%
                    </div>
                </div>
            </div>

            <!-- Bowling : la question n'est pas posée — le mode est TOUJOURS « par groupe » (décision
                 2026-09-15, imposée aussi par le serveur). `lockBowlingPricingMode` le pose quand même. -->
            <SelectableList
                v-if="!bowlingPricingLocked"
                :label="$t('experience.step5.reservationMode.title')"
                v-model="selectedOptionTarifMode"
                :options="options_tarif_mode"
                :disabled="isPublishedPrestation || isPricingModeDisabled"
                :tooltipMsg="pricingModeTooltip"
                :required="true"
                :errorText="errors.selectedOptionTarifMode"
                @input="
                    activeField = 'reservationMode'
                    clearError('selectedOptionTarifMode')
                "
            />

            <!-- Min/Max People inputs (hidden when circuit forces both values) -->
            <div
                v-if="showMinTicket || showMaxTicket"
                class="ppv2-flex ppv2-items-center ppv2-mt-5 ppv2-mb-5"
                :style="{ gap: normalizeSize(16, true) }"
            >
                <CustomInput
                    v-if="showMinTicket"
                    v-model="min_ticket"
                    :required="true"
                    :label="$t('experience.step7.tickets_possibles.min')"
                    :state="getState('min_ticket')"
                    :errorText="errors.min_ticket"
                    type="number"
                    :min="minTicketMinValue"
                    placeholder=""
                    @input="clearError('min_ticket')"
                    @focus="activeField = 'min_ticket'"
                    classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                    :customSizeLabel="12"
                    customClassLabel="poppinsMedium"
                />

                <CustomInput
                    v-if="showMaxTicket"
                    v-model="max_ticket"
                    :required="true"
                    :label="$t('experience.step7.tickets_possibles.max')"
                    :state="getState('max_ticket')"
                    type="number"
                    :min="-1"
                    placeholder=""
                    @input="clearError('max_ticket')"
                    @focus="activeField = 'max_ticket'"
                    :errorText="errors.max_ticket"
                    classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                    :customSizeLabel="12"
                    customClassLabel="poppinsMedium"
                />
            </div>

            <!-- taux tva -->
            <div
                v-if="!isSimpleTicketCircuit"
                class="gestion_creneau ppv2-flex ppv2-flex-wrap ppv2-items-center ppv2-border ppv2-border-[#545595] ppv2-rounded-[12px] ppv2-text-black ppv2-mb-2 ppv2-px-3 ppv2-min-h-[54px]"
            >
                <label
                    class="ppv2-truncate"
                    :style="{
                        fontSize: normalizeFontSize(12),
                        width: 'calc(100% - ' + normalizeSize(175, true) + ')',
                        minWidth: 'max-content',
                    }"
                >
                    {{ $t('experience.step5.price.Question') }}
                </label>

                <div class="ppv2-mr-3" :style="{ width: normalizeSize(160, true) }">
                    <CustomInput
                        v-model="price"
                        :right-icon="$symbolCurrency"
                        :required="true"
                        :state="getState('price')"
                        :errorText="errors.price"
                        type="number"
                        :min="0"
                        @input="
                            clearError('price')
                            syncPriceToBackend()
                        "
                        classInputAdd="!ppv2-min-h-[30px] !ppv2-h-[30px] !ppv2-rounded-lg "
                        classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                        :customSizeLabel="10"
                        customClassLabel="poppinsMedium"
                        @focus="activeField = 'price'"
                    />
                </div>
            </div>

            <!-- Taux TVA (Restored) -->
            <div
                v-if="!isSimpleTicketCircuit"
                class="gestion_creneau ppv2-flex ppv2-flex-wrap ppv2-items-center ppv2-border ppv2-border-[#545595] ppv2-rounded-[12px] ppv2-text-black ppv2-mb-2 ppv2-px-3 ppv2-min-h-[54px]"
            >
                <label
                    class="ppv2-truncate"
                    :style="{
                        fontSize: normalizeFontSize(12),
                        width: 'calc(100% - ' + normalizeSize(175, true) + ')',
                        minWidth: 'max-content',
                    }"
                >
                    {{ $t('experience.step5.tva.Question') }}
                </label>

                <div class="ppv2-mr-3" :style="{ width: normalizeSize(160, true) }">
                    <BaseSelect
                        compact
                        v-model="selected_taux_tva"
                        :options="option_tva"
                        :searchable="false"
                        option-label="label"
                        option-value="value"
                        :selectable="(option) => option.value !== ''"
                        class="transparent-select ppv2-w-full"
                        class-added="!ppv2-rounded-lg !ppv2-h-[30px]"
                        :error="errors.selected_taux_tva"
                        @input="
                            clearError('selected_taux_tva')
                            syncPriceToBackend()
                        "
                    />
                </div>
            </div>

            <template v-if="!isSimpleTicketCircuit && !isEventCircuit">
                <div
                    v-if="!isOn"
                    class="ppv2-relative"
                    :style="{ marginTop: normalizeSizeVertical(27, true), marginBottom: normalizeSizeVertical(20) }"
                >
                    <div class="ppv2-mx-[10px] ppv2-px-1 ppv2-bg-white dark:ppv2-bg-dark">
                        <BaseSwitch v-model="isOn" :label="$t('experience.step5.multiMode.schedule.configSchedule')" />
                    </div>
                </div>

                <DashedCard v-else :marginTop="37" :marginBottom="30" :custom-style="{ padding: normalizeSize(24, true) }">
                    <template #title>
                        <BaseSwitch v-model="isOn" :label="$t('experience.step5.multiMode.schedule.configSchedule')" />
                    </template>
                    <template #content>
                        <TarifItem
                            v-if="isOn"
                            v-for="(tarifItem, index) in tarifs"
                            :key="tarifItem._uid || index"
                            :durations="getFilteredDurations(tarifItem.duree)"
                            :tierDurations="getTierDurationOptions()"
                            :hasDynamicDuration="hasDynamicDuration"
                            ref="tarifForm"
                            :tarif="tarifItem"
                            :isFirst="index === 0"
                            :optionIndex="index + 1"
                            @duplicate="duplicateTarif(index)"
                            @duplicateDegressifTarif="duplicateDegressifTarif(index)"
                            @remove="removeTarif(index)"
                            @focus="activeField = 'tarif'"
                            @input="tarifs = [...tarifs]"
                            :prestation="prestation"
                            :id="id"
                        />
                        <!-- + Ajouter une option -->
                        <button v-if="isOn" type="button" class="add-option-btn" @click="duplicateTarif(tarifs.length - 1)">
                            <Plus :size="16" />
                            <span class="poppinsMedium">{{ $t('experience.add_option') }}</span>
                        </button>
                    </template>
                </DashedCard>
            </template>
            <!-- ============================================================ -->
            <!-- CALENDAR PLANNING (replaces old "Tarifs spécifiques" section) -->
            <!-- ============================================================ -->
            <div
                v-if="!isTarifByDayHours && !isSimpleTicketCircuit && !isEventCircuit && localPrestation && localPrestation.id"
                class="ppv2-relative"
                :style="{ marginTop: normalizeSizeVertical(27, true), marginBottom: normalizeSizeVertical(20, true) }"
            >
                <div class="ppv2-mx-[10px] ppv2-px-1 ppv2-bg-white dark:ppv2-bg-dark">
                    <!-- Bowling : activable seulement si le prix par personne est identique sur tous les
                         paliers. Tooltip sur un activateur `div` : un élément grisé ne reçoit pas le survol. -->
                    <v-tooltip bottom :disabled="specificPricingEligible" max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                            <div class="ppv2-inline-block" v-bind="attrs" v-on="on">
                                <BaseSwitch
                                    v-model="isTarifByDayHours"
                                    :disabled="
                                        reservationMode('free_access_all_day') ||
                                        reservationMode('one_schedule_for_everyone') ||
                                        !specificPricingEligible
                                    "
                                    :label="$t('experience.step5.multiMode.schedule.configTarif')"
                                />
                            </div>
                        </template>
                        <span>{{ $t('experience.step5.specificPricingUniformPriceOnly') }}</span>
                    </v-tooltip>
                </div>
            </div>
            <DashedCard
                v-else-if="
                    isTarifByDayHours && !isSimpleTicketCircuit && !isEventCircuit && localPrestation && localPrestation.id
                "
                :marginTop="37"
                :marginBottom="30"
                :custom-style="{ padding: normalizeSize(37, true) }"
            >
                <template #title>
                    <BaseSwitch
                        v-model="isTarifByDayHours"
                        :disabled="reservationMode('free_access_all_day')"
                        :label="$t('experience.step5.multiMode.schedule.configTarif')"
                    />
                </template>
                <template #content>
                    <!-- Tarifs déjà configurés mais paliers devenus différents : le switch reste
                         éteignable (ce qui purge les tarifs), l'enregistrement est bloqué. -->
                    <p
                        v-if="!specificPricingEligible || specificPricingServerError"
                        class="ppv2-text-[#ed4b9e] ppv2-text-sm ppv2-mb-4"
                    >
                        {{ specificPricingServerError || $t('experience.step5.specificPricingUniformPriceOnly') }}
                    </p>
                    <CalendarFull
                        ref="planningCalendar"
                        :subCategoryId="localPrestation && localPrestation.subcategory_id"
                        :establishmentId="1"
                        :prestation="localPrestation"
                        :fetchUrl="planningFetchUrl"
                        @date-click="onPlanningDateClick"
                        @event-click="onPlanningEventClick"
                        @checked-dates-change="planningCheckedDates = $event"
                    />

                    <div v-if="planningCheckedDates.length > 0" class="ppv2-flex ppv2-justify-center ppv2-mt-4">
                        <BaseButton
                            type="button"
                            variant="secondary"
                            customClass="!ppv2-rounded-full !ppv2-px-8 !ppv2-border-none !ppv2-bg-[#D1D5DB] dark:!ppv2-bg-[#2c2c54] !ppv2-text-[#11102A] dark:!ppv2-text-[#FFFFFF]"
                            :customHeight="43"
                            :customFontSize="12"
                            @click="openPlanningManual"
                        >
                            {{ $t('experience.planningModal.configureBtn') }}
                        </BaseButton>
                    </div>

                    <PlanningModal
                        v-model="showPlanningModal"
                        :prestationId="localPrestation.id"
                        :establishmentId="1"
                        :selectedSlot="planningSelectedSlot"
                        :mode="planningMode"
                        :prestation="localPrestation"
                        :existingTarifs="configuredTarifs"
                        @refresh="onPlanningRefresh"
                        @cancel="showPlanningModal = false"
                    />
                </template>
            </DashedCard>
            <div
                v-if="!isAccompte"
                class="ppv2-relative"
                :style="{ marginTop: normalizeSizeVertical(27, true), marginBottom: normalizeSizeVertical(20, true) }"
            >
                <div class="ppv2-mx-[10px] ppv2-px-1 ppv2-bg-white dark:ppv2-bg-dark">
                    <BaseSwitch
                        v-model="isAccompte"
                        :label="$t('experience.step5.multiMode.schedule.accompte')"
                        @change="activeField = 'accompte'"
                    />
                </div>
            </div>

            <DashedCard v-else :marginTop="37" :marginBottom="30" :custom-style="{ padding: normalizeSize(24, true) }">
                <template #title>
                    <BaseSwitch
                        v-model="isAccompte"
                        :label="$t('experience.step5.multiMode.schedule.accompte')"
                        @change="activeField = 'accompte'"
                    />
                </template>
                <template #content>
                    <AcompteForm ref="acompteForm" v-if="isAccompte" v-model="acompte" :price="price" />
                </template>
            </DashedCard>

            <div class="form-actions" :class="{ '!ppv2-justify-end': this.complited }">
                <!-- Submit button -->
                <BackButton v-show="!this.complited" type="button" @click="$emit('previous-step')" />
                <BaseButton id="submit" type="submit" variant="primary" @click="submitForm" :loading="loading">
                    {{ !this.complited ? $t('experience.next') : $t('common.save') }}
                </BaseButton>
            </div>
        </div>
        <PreviewExperience :experience="prestation" :activeField="activeField" :step="step" />
    </div>
</template>

<script>
import SelectableList from '../../../Shared/SelectableList.vue'
import TarifItem from '../../../Shared/TarifItem.vue'
import HeaderStep from './HeaderStep.vue'
import AcompteForm from './AcompteForm.vue'
import ListTarifSpecifique from './ListTarifSpecifique.vue'
import CalendarFull from '../../../Shared/CalendarFull.vue'
import PlanningModal from './PlanningModal.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import BackButton from '../../../Shared/BackButton.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import { Plus, Calendar } from 'lucide-vue'
import CustomButtonSave from '../../../Shared/CustomButtonSave.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeSizeVertical from '../../../../utils/normalizeSizeVertical'
import DashedCard from '../../../Shared/DashedCard.vue'
import PreviewExperience from './PreviewExperience.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import CustomInput from '../../../Inputs/CustomInput.vue'
import { EventBus } from '../../../../eventBus'
import ExperienceMixin from '../mixins/ExperienceMixin'
import { SENTINEL_END_DATE } from '../../../../constants/planning'
import { circuitDetectionService, CIRCUIT_TYPES, CONFIDENCE_LEVELS } from '../../../../services/CircuitDetectionService'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import { uniformPricePerPerson } from '../../../../services/tierPricing'

export default {
    components: {
        BaseSelect,
        PreviewExperience,
        DashedCard,
        CustomButtonSave,
        HeaderStep,
        SelectableList,
        TarifItem,
        AcompteForm,
        BaseButton,
        BackButton,
        BaseSwitch,
        ListTarifSpecifique,
        CalendarFull,
        PlanningModal,
        Plus,
        Calendar,
        CustomInput,
    },
    mixins: [ExperienceMixin],
    props: {
        step: { type: Object, required: true },
        sizes: { type: Object, required: true },
        prestation: { type: Object, required: false },
        selected_experience: { type: Object, required: false },
        id: { type: [Number, String], default: null },
        category: {
            type: Object,
            required: false,
        },
        sub_category: {
            type: Object,
            required: false,
        },
        country_vat_rates: {
            type: Array,
            required: false,
        },
        complited: {
            type: Boolean,
            required: false,
        },
    },
    data() {
        return {
            activeField: null,
            selectedOptionTarifMode: null,
            selectedManyOption: null,
            selected_taux_tva: '',
            options_tarif_mode: [
                {
                    label: this.$t('experience.step5.tarif_mode.person.label'),
                    value: 'price per person',
                    subtitle: this.$t('experience.step5.tarif_mode.person.subtitle'),
                    disabled: false,
                },
                {
                    label: this.$t('experience.step5.tarif_mode.group.label'),
                    value: 'price per group',
                    subtitle: this.$t('experience.step5.tarif_mode.group.subtitle'),
                    disabled: false,
                },
            ],
            many_options: [
                {
                    label: this.$t('experience.step5.many_options.no.label'),
                    value: 'non',
                    subtitle: this.$t('experience.step5.many_options.no.subtitle'),
                },
                {
                    label: this.$t('experience.step5.many_options.yes.label'),
                    value: 'oui',
                    subtitle: this.$t('experience.step5.many_options.yes.subtitle'),
                },
            ],
            isOn: false,
            rules: {
                required: (value) => !!value || this.$t('experience.errors.required'),
            },
            // tarifs moved to store
            isTarifByDayHours: false,
            configuredTarifs: [],

            localPrestation: null,
            isAccompte: true,
            acompte: {
                type: null,
                value: null,
            },
            originalData: null,
            option_tva: [],
            price: '',
            errors: {},
            lastChangedPricingField: null,
            _syncingFromPrice: false,
            _syncingFromTarif: false,
            pendingPriceRequestSequence: 0,
            pendingPriceRequestPromise: null,
            priceSyncQueue: Promise.resolve(),

            isAutoChange: false,
            horaires_specifiques: [],
            events: [],
            // Planning calendar
            showPlanningModal: false,
            planningSelectedSlot: null,
            planningMode: 'create',
            planningCheckedDates: [],

            // Circuit Detection Service constants
            CIRCUIT_TYPES,
            CONFIDENCE_LEVELS,
            loading: false,
            // Delete confirmation state
            showDeletePopup: false,
            deletePopupTitle: '',
            deletePopupDescription: '',
            itemToDelete: null,
            deleteAction: '',
            initialDataString: null,
        }
    },
    methods: {
        hasConfiguredOptions() {
            const blocks = this.$store.getters['experience/dynamicDurationBlocks'] || []
            return (
                blocks.length > 1 ||
                blocks.some((block) => Array.isArray(block.prices_degressif) && block.prices_degressif.length > 0)
            )
        },
        normalizeFontSize,
        normalizeSize,
        normalizeSizeVertical,
        duplicateTarif(index) {
            const tarifs = [...this.tarifs]
            const firstTarif = tarifs[index]

            // Create a copy but reset specific fields.
            // Même durée que la source : deux options peuvent partager une durée. `id: null` = NOUVELLE
            // option — recopier l'id ferait viser la même ligne en base aux deux tarifs.
            const newTarif = {
                ...firstTarif,
                id: null,
                name: '', // empty
                price: '', // empty
                duree: firstTarif.duree,
                editing: false,
                prices_degressif: [], // empty array
                _uid: Date.now() + '_' + Math.random().toString(36).substr(2, 9),
            }
            // Insert the new item after the original
            tarifs.splice(index + 1, 0, newTarif)
            this.tarifs = tarifs
        },
        removeTarif(index) {
            const tarifs = [...this.tarifs]
            tarifs.splice(index, 1)
            if (tarifs.length === 0) {
                const availableOptions = this.getFilteredDurations('')
                const defaultOption = availableOptions.find((o) => o.value !== -1)
                const defaultDuree = defaultOption ? defaultOption.value : ''

                tarifs.push({
                    name: '',
                    price: '',
                    duree: defaultDuree,
                    editing: false,
                    prices_degressif: [],
                    _uid: Date.now() + '_' + Math.random().toString(36).substr(2, 9),
                })
            }
            this.tarifs = tarifs
        },
        duplicateDegressifTarif(index) {
            const tarifs = [...this.tarifs]
            const tarif = tarifs[index]
            // Determine next persons value by incrementing the max existing persons
            const existing = Array.isArray(tarif.prices_degressif) ? tarif.prices_degressif : []

            let nextPersons = 1
            if (existing.length > 0) {
                const persons = existing.map((d) => {
                    const v = Number(d.persons)
                    return Number.isNaN(v) ? 0 : v
                })
                nextPersons = Math.max(...persons) + 1
            } else {
                const minVal = Number(this.min_ticket)
                nextPersons = !Number.isNaN(minVal) && minVal > 0 ? minVal : 1
            }

            // Prix du dernier palier par défaut. `parseFloat('')` vaut NaN et `'NaN'` est une chaîne
            // truthy : sans le garde, un palier sans prix en engendrait un à « NaN ».
            const last = existing.length ? existing[existing.length - 1] : null
            const lastParsed = parseFloat(last?.price)
            const lastPrice = Number.isFinite(lastParsed) && lastParsed > 0 ? lastParsed.toFixed(2) : ''
            // La duplication reprend durée ET prix (demande du ticket). « Personnalisée » (-1) porte
            // sa vraie durée dans `personilize` : sans la recopier, le nouveau palier naît avec un -1
            // orphelin, donc en erreur immédiate.
            const lastDuration = last ? last.duration || '' : ''
            const lastPersonilize = last && Number(last.duration) === -1 ? last.personilize || '' : ''

            const newDegressif = {
                persons: nextPersons,
                duration: lastDuration,
                personilize: lastPersonilize,
                price: lastPrice,
                editing: false,
            }

            if (!Array.isArray(tarif.prices_degressif)) {
                tarif.prices_degressif = []
            }

            tarif.prices_degressif.push(newDegressif)
            this.tarifs = tarifs
        },
        updateTarif(index, updatedTarif) {
            this.$set(this.configuredTarifs, index, updatedTarif)
        },

        // Canonical key of a tarif scenario: same dates + same hours = duplicate.
        tarifKey(t) {
            return [t.dateStart, t.dateEnd || '', t.timeStart, t.timeEnd].join('|')
        },
        isDuplicateTarif(candidate, ignoreIndex = -1) {
            const key = this.tarifKey(candidate)
            return this.configuredTarifs.some((t, i) => i !== ignoreIndex && this.tarifKey(t) === key)
        },

        addConfiguredTarif(newTarif) {
            if (this.isDuplicateTarif(newTarif)) {
                this.$toast.error(this.$t('experience.step5.errors.duplicateTarif'))
                return
            }
            this.configuredTarifs.push(newTarif)
        },

        deleteTarif(index) {
            this.configuredTarifs.splice(index, 1)
        },
        duplicateTarifList(tarif, index) {
            const duplicated = { ...tarif, tmp_id: Date.now() }
            // An identical scenario (same dates + hours) cannot exist twice.
            if (this.isDuplicateTarif(duplicated)) {
                this.$toast.error(this.$t('experience.step5.errors.duplicateTarif'))
                return
            }
            this.configuredTarifs.splice(index + 1, 0, duplicated)
        },
        dedupePlanningPrices(prices) {
            const seen = new Set()
            return (prices || []).filter((p) => {
                const k = `${(p.start_time || '').substring(0, 5)}|${(p.end_time || '').substring(0, 5)}`
                if (seen.has(k)) return false
                seen.add(k)
                return true
            })
        },
        formatHour(time) {
            return parseInt(time.split(':')[0], 10) + 'h'
        },
        formatDateFr(dateStr) {
            const options = { day: 'numeric', month: 'long', year: 'numeric' }
            return new Date(dateStr).toLocaleDateString('fr-FR', options)
        },
        formatDays(daysArray) {
            // Flatten: selectedDays may contain comma-joined strings like "Monday, Tuesday"
            const flat = daysArray
                .flatMap((d) => (typeof d === 'string' ? d.split(',').map((s) => s.trim()) : [d]))
                .filter(Boolean)

            const weekdays = {
                Monday: this.$t('resource_planning.week_days.monday'),
                Tuesday: this.$t('resource_planning.week_days.tuesday'),
                Wednesday: this.$t('resource_planning.week_days.wednesday'),
                Thursday: this.$t('resource_planning.week_days.thursday'),
                Friday: this.$t('resource_planning.week_days.friday'),
                Saturday: this.$t('resource_planning.week_days.saturday'),
                Sunday: this.$t('resource_planning.week_days.sunday'),
            }

            if (flat.length === 7) return this.$t('common.allDays')
            if (flat.length === 5 && !flat.includes('Saturday') && !flat.includes('Sunday'))
                return this.$t('common.allDaysNoWeekend')

            return flat.map((d) => weekdays[d] || d).join(', ')
        },
        async saveAndExit() {
            this.$emit('save-and-exit')
        },
        // ── Planning Calendar methods ──
        // Fallback recovery: rebuild planningPrices for the given dates from configuredTarifs
        // (the source of truth loaded from the API on edit). The calendar only exposes prices
        // for dates inside the currently fetched window, so a checked day outside that window —
        // or before the calendar (re)fetches — yields nothing and the modal would open blank.
        // Matching is purely by date range: configuredTarifs cover continuous [dateStart, dateEnd]
        // ranges (selectedDays is just those weekdays enumerated), so a per-weekday filter would
        // be redundant — and avoids a timezone mismatch with getDaysBetween's weekday derivation.
        recoverPlanningPricesFromConfig(dates) {
            if (!dates || !dates.length || !this.configuredTarifs.length) return null
            const covers = (t, d) => {
                const ds = (t.dateStart || '').substring(0, 10)
                const de = (t.dateEnd || '').substring(0, 10)
                return (!ds || d >= ds) && (!de || d <= de)
            }
            const matches = this.configuredTarifs.filter((t) => dates.some((d) => covers(t, d)))
            if (!matches.length) return null
            // Most-specific first: a finite/narrow range wins over an open-ended base when both cover a
            // date, so dedupePlanningPrices (first-wins) and matches[0].planningId pick the right tarif.
            const span = (t) => {
                const ds = (t.dateStart || '').substring(0, 10)
                const de = (t.dateEnd || '').substring(0, 10)
                if (!ds || !de) return Infinity
                return new Date(de).getTime() - new Date(ds).getTime()
            }
            matches.sort((a, b) => span(a) - span(b))
            const planningPrices = this.dedupePlanningPrices(
                matches.map((t) => {
                    const ttc = parseFloat(t.price)
                    const vat = parseFloat(t.vatRate)
                    return {
                        start_time: t.timeStart,
                        end_time: t.timeEnd,
                        price: t.price,
                        // configuredTarifs drops price_ht on load; recompute it (mirrors
                        // PlanningModal.calculatePrice) so saving via this fallback path doesn't
                        // persist a blank HT, unlike the normal calendar-event path.
                        price_ht: isNaN(ttc) || isNaN(vat) ? '' : (ttc / (1 + vat / 100)).toFixed(2),
                        vat_rate: t.vatRate,
                        authorize_mixing: t.authorize_mixing,
                        off_peak_time: t.off_peak_time,
                    }
                })
            )
            if (!planningPrices.length) return null
            return { planningPrices, planningId: matches[0].planningId || null }
        },
        buildSlotFromCheckedDates(fallbackDate) {
            let firstDate = null
            let slot = null
            let sorted = []

            if (this.planningCheckedDates.length > 0) {
                sorted = [...this.planningCheckedDates].sort()
                firstDate = sorted[0]
                slot = {
                    dateStr: sorted[0],
                    start: sorted[0],
                    startStr: sorted[0],
                    end: sorted[sorted.length - 1],
                    endStr: sorted[sorted.length - 1],
                }
            } else if (fallbackDate) {
                sorted = [fallbackDate]
                firstDate = fallbackDate
                slot = {
                    dateStr: fallbackDate,
                    start: fallbackDate,
                    startStr: fallbackDate,
                    end: fallbackDate,
                    endStr: fallbackDate,
                }
            }

            if (slot && this.$refs.planningCalendar) {
                const apiEvents = this.$refs.planningCalendar.currentApiEvents || []
                const allMatches = apiEvents.filter((e) => {
                    const eDate = (e.start || '').substring(0, 10)
                    return sorted.includes(eDate) && e.planningPrices && e.planningPrices.length > 0
                })
                const mergedPrices = this.dedupePlanningPrices(allMatches.flatMap((e) => e.planningPrices))
                if (mergedPrices.length > 0) {
                    slot.planningPrices = mergedPrices
                }
            }

            // Calendar events didn't expose saved prices for these dates → recover from configuredTarifs
            // so the modal pre-fills the start/end times instead of opening blank (00:00). This is a
            // create-mode slot (openPlanningManual/onPlanningDateClick): pre-fill the créneaux only —
            // do NOT inherit a planningId here, or syncPlanningModalToConfiguredTarifs would purge
            // every sibling tarif sharing it.
            if (slot && !(slot.planningPrices && slot.planningPrices.length)) {
                const recovered = this.recoverPlanningPricesFromConfig(sorted)
                if (recovered) {
                    slot.planningPrices = recovered.planningPrices
                }
            }

            return slot
        },
        openPlanningManual() {
            this.planningMode = 'create'
            this.planningSelectedSlot = this.buildSlotFromCheckedDates(null)
            this.showPlanningModal = true
        },
        onPlanningDateClick(info) {
            this.planningSelectedSlot = this.buildSlotFromCheckedDates(info.dateStr)
            this.planningMode = 'create'
            this.showPlanningModal = true
        },
        onPlanningEventClick(info) {
            this.planningMode = 'edit'
            const eventDate = (info.event.startStr || '').substring(0, 10)
            const apiEvents = this.$refs.planningCalendar?.currentApiEvents || []
            const allEventsOnDate = apiEvents.filter((e) => {
                const eDate = (e.start || '').substring(0, 10)
                return eDate === eventDate && e.planningPrices && e.planningPrices.length > 0
            })
            let mergedPrices = this.dedupePlanningPrices(allEventsOnDate.flatMap((e) => e.planningPrices))
            const firstEvent = allEventsOnDate[0]
            let planningId = firstEvent?.planningId || firstEvent?.extendedProps?.planningId || null
            const planningStartDate =
                firstEvent?.extendedProps?.planningStartDate || firstEvent?.start?.substring(0, 10) || eventDate
            const planningEndDate = firstEvent?.extendedProps?.planningEndDate ?? null

            // Fallback: calendar event carried no prices → recover the saved tarif from configuredTarifs
            // so start/end times pre-fill instead of opening blank.
            if (!mergedPrices.length) {
                const recovered = this.recoverPlanningPricesFromConfig([eventDate])
                if (recovered) {
                    mergedPrices = recovered.planningPrices
                    planningId = planningId || recovered.planningId
                }
            }

            // Get selectedDays from configuredTarifs by date-range overlap
            // (planningId alone can't be used because groupConsecutiveTarifs merges rows
            // and keeps only the first row's planningId)
            let selectedDays = []
            const matchingTarif = this.configuredTarifs.find((t) => {
                const ds = (t.dateStart || '').substring(0, 10)
                const de = (t.dateEnd || ds).substring(0, 10)
                return eventDate >= ds && eventDate <= de
            })
            if (matchingTarif && matchingTarif.selectedDays) {
                selectedDays = [...matchingTarif.selectedDays]
            }

            this.planningSelectedSlot = {
                dateStr: eventDate,
                start: planningStartDate,
                startStr: planningStartDate,
                end: planningEndDate || eventDate,
                endStr: planningEndDate || eventDate,
                planningPrices: mergedPrices,
                planningId: planningId,
                selectedDays: selectedDays,
            }
            this.showPlanningModal = true
        },
        onPlanningRefresh(payload) {
            if (this.$refs.planningCalendar) {
                const api = this.$refs.planningCalendar.$refs.fullCalendar?.getApi()
                if (api) api.refetchEvents()
            }
            if (payload) {
                this.syncPlanningModalToConfiguredTarifs(payload)
            }
        },
        syncPlanningModalToConfiguredTarifs(payload) {
            const { days, range, items, planningId } = payload
            if (!items || !items.length) return
            const endDate = range.endDate || null

            const newEntries = items.map((item) => ({
                dateStart: range.startDate,
                dateEnd: endDate,
                timeStart: item.startTime ? item.startTime.substring(0, 5) : '00:00',
                timeEnd: item.endTime ? item.endTime.substring(0, 5) : '00:00',
                price: parseFloat(item.price || 0).toFixed(2),
                // A créneau never carries its own VAT (PLPRB-2501): the server always stores
                // the prestation's rate, so the local mirror shows that same rate. Reading it
                // back from the item would resurrect a stale per-créneau value on screen.
                vatRate: this.selected_taux_tva,
                authorize_mixing: item.authorize_mixing !== undefined ? !!item.authorize_mixing : true,
                off_peak_time: item.off_peak_time !== undefined ? !!item.off_peak_time : true,
                selectedDays: days && days.length ? days : this.getDaysBetween(range.startDate, endDate, 'en').split(', '),
                planningId: planningId || null,
            }))

            // Remove existing tarifs that overlap with the new submission's date range.
            // Can't rely on planningId alone because groupConsecutiveTarifs merges rows and
            // keeps only the first row's planningId.
            const newStart = (range.startDate || '').substring(0, 10)
            const newEnd = (endDate || newStart).substring(0, 10)
            const keptBase = this.configuredTarifs.filter((e) => {
                const eStart = (e.dateStart || '').substring(0, 10)
                const eEnd = (e.dateEnd || eStart).substring(0, 10)
                return !(eStart <= newEnd && eEnd >= newStart)
            })

            // Reject scenarios that already exist (same dates + hours), in the kept base or within this batch.
            const baseKeys = new Set(keptBase.map((e) => this.tarifKey(e)))
            const seen = new Set()
            const dedupedNew = newEntries.filter((e) => {
                const k = this.tarifKey(e)
                if (baseKeys.has(k) || seen.has(k)) return false
                seen.add(k)
                return true
            })

            if (dedupedNew.length < newEntries.length) {
                this.$toast.error(this.$t('experience.step5.errors.duplicateTarif'))
            }

            this.configuredTarifs = this.groupConsecutiveTarifs([...keptBase, ...dedupedNew])

            // Le modal persiste déjà via storePlanning (sauvegarde directe backend) :
            // seul le miroir configuredTarifs est à jour côté serveur — on réaligne
            // uniquement cette partie de la baseline, sinon on absorberait aussi les
            // modifications non enregistrées (prix, TVA, min/max, acompte…) et le popup
            // « modifications » ne s'afficherait plus alors que le prix est perdu.
            // L'ordre des clés est conservé via JSON.parse/stringify, donc hasChanges()
            // reste valide.
            if (!this.initialDataString) {
                this.initialDataString = this.getFormDataString()
            } else {
                const base = JSON.parse(this.initialDataString)
                base.configuredTarifs = this.configuredTarifs
                this.initialDataString = JSON.stringify(base)
            }
        },
        syncCalendarDefaultPrices(newPrice) {
            const calendarRef = this.$refs.planningCalendar?.$refs?.fullCalendar
            if (!calendarRef) return
            const api = calendarRef.getApi()
            if (!api) return
            const formatted = parseFloat(newPrice)
            if (isNaN(formatted)) return
            const label = formatted.toFixed(2) + ' ' + this.$symbolCurrency
            api.getEvents().forEach((event) => {
                if (typeof event.id === 'string' && event.id.startsWith('default-')) {
                    event.setProp('title', label)
                    event.setExtendedProp('eventName', label)
                }
            })
        },
        async syncPriceToBackend() {
            const id = this.localPrestation?.id
            if (!id) return
            const price = parseFloat(this.price)
            const vatRate = parseFloat(this.selected_taux_tva)
            if (isNaN(price) || price < 0 || isNaN(vatRate) || vatRate < 0) return
            const requestSequence = ++this.pendingPriceRequestSequence
            let request = null
            try {
                request = this.priceSyncQueue.then(() =>
                    axios.patch(`/api/v3/experience/${id}/sync-price`, {
                        price,
                        vat_rate: vatRate,
                    })
                )
                this.priceSyncQueue = request.catch(() => null)
                this.pendingPriceRequestPromise = this.priceSyncQueue
                await request
                if (requestSequence !== this.pendingPriceRequestSequence) return

                this.localPrestation = {
                    ...this.localPrestation,
                    price,
                    vat_rate: vatRate,
                }
                this.$store.dispatch('experience/setPrice', price.toFixed(2))
                this.$emit('update-prestation', this.localPrestation)
                this.onPlanningRefresh()
            } catch (e) {
                console.error('sync-price error:', e)
            } finally {
                if (requestSequence === this.pendingPriceRequestSequence) this.pendingPriceRequestPromise = null
            }
        },
        /**
         * Minimum / maximum de billets — règle unique, appelée par le bouton « Enregistrer » ET par
         * saveExperience (« Enregistrer et quitter » du bandeau, qui ne passait par aucune validation :
         * un minimum < 1 partait au serveur et revenait en 422). Champ masqué (booking, billetterie) :
         * la valeur est forcée à 1, comme adaptMinMaxForCircuit l'entend — le serveur exige min:1.
         */
        validateTickets() {
            if (!this.showMinTicket && Number(this.min_ticket) < 1) {
                this.$store.dispatch('experience/setMinTicket', 1)
            }

            const minVal = Number(this.min_ticket)
            const maxVal = Number(this.max_ticket)

            // Skip validation for hidden fields (values are auto-forced)
            if (this.showMinTicket) {
                if (this.min_ticket === null || this.min_ticket === '' || isNaN(minVal)) {
                    this.$set(this.errors, 'min_ticket', this.$t('experience.errors.required'))
                } else if (minVal < 1) {
                    this.$set(this.errors, 'min_ticket', this.$t('experience.step5.errors.minTicketOne'))
                } else if (maxVal > 0 && minVal > 0 && minVal > maxVal) {
                    this.$set(this.errors, 'min_ticket', this.$t('experience.step5.errors.minGreaterThanMax'))
                } else {
                    this.$delete(this.errors, 'min_ticket')
                }
            } else {
                this.$delete(this.errors, 'min_ticket')
            }

            if (this.showMaxTicket) {
                if (this.max_ticket === null || this.max_ticket === '' || isNaN(maxVal)) {
                    this.$set(this.errors, 'max_ticket', this.$t('experience.errors.required'))
                } else if (maxVal > 0 && minVal > 0 && maxVal < minVal) {
                    this.$set(this.errors, 'max_ticket', this.$t('experience.step5.errors.maxLessThanMin'))
                } else {
                    this.$delete(this.errors, 'max_ticket')
                }
            } else {
                this.$delete(this.errors, 'max_ticket')
            }

            return !this.errors.min_ticket && !this.errors.max_ticket
        },
        async submitForm() {
            // ✅ FIX: Reset ALL errors (clears stale backend keys from previous 422)
            this.errors = {}
            this.error = null

            // ✅ FIX: Validate selectedOptionTarifMode is required
            if (!this.selectedOptionTarifMode) {
                this.$set(this.errors, 'selectedOptionTarifMode', this.$t('experience.step5.errors.requiredPricingMode'))
                this.error = this.$t('experience.step5.errors.requiredPricingMode')
            } else {
                this.$delete(this.errors, 'selectedOptionTarifMode')
            }

            this.validateTickets()

            // Validate price is required
            if (!this.isSimpleTicketCircuit) {
                if (this.price === null || this.price === '' || this.price === undefined || Number(this.price) < 0) {
                    this.$set(this.errors, 'price', this.$t('experience.errors.required'))
                } else {
                    this.$delete(this.errors, 'price')
                }
            }

            // Validate TVA is required
            if (!this.isSimpleTicketCircuit) {
                if (this.selected_taux_tva === null || this.selected_taux_tva === '' || this.selected_taux_tva === undefined) {
                    this.$set(this.errors, 'selected_taux_tva', this.$t('experience.errors.required'))
                } else {
                    this.$delete(this.errors, 'selected_taux_tva')
                }
            }

            if (Object.keys(this.errors).length === 0) {
                const success = await this.saveExperience(false)
                if (success) {
                    this.$toast.success(this.$t('experience.saved'))
                    if (!this.complited) {
                        this.$emit('next-step')
                    }
                }
            }
        },
        async saveExperience(redirect = false) {
            this.error = null
            if (!this.validateTickets()) return false
            if (this.pendingPriceRequestPromise) await this.pendingPriceRequestPromise
            const submittedPrice = this.price
            //is accompte selected
            if (this.isAccompte) {
                const isValid = this.$refs.acompteForm.validate()
                if (!isValid) return false
            }

            if (this.isOn && !this.isSimpleTicketCircuit && !this.isEventCircuit) {
                const tarifForms = Array.isArray(this.$refs.tarifForm) ? this.$refs.tarifForm : [this.$refs.tarifForm]
                const allValid = tarifForms.every((tarifComponent) => tarifComponent?.validateTarifs?.())
                if (!allValid) {
                    return false
                }
            }

            // Bowling : tarifs spécifiques actifs mais prix par personne devenus différents. Le message
            // est affiché dans la carte des tarifs spécifiques ; le serveur refuse de toute façon.
            if (this.isTarifByDayHours && !this.specificPricingEligible) {
                return false
            }

            // Start loading only after validations pass
            this.loading = true

            // Si les tarifs dégressifs ne sont pas activés, vider les tarifs côté front
            if (!this.isOn) {
                this.$store.dispatch('experience/setDynamicDurationBlocks', [])
            }

            // Primary detected type uses exact matching (not scoring) for backend accuracy
            const primaryDetectedType = this.$store.getters['experience/getPrimaryDetectedType']
            const userSelections = this.$store.getters['experience/userSelections']

            const data_accompte = {
                isAccompte: this.isAccompte,
                acompte: {
                    type: this.acompte.type,
                    value: parseFloat(this.acompte.value) || 0,
                },
            }
            const formData = {
                step: this.step.id,
                selectedOptionReservationMode: this.selectedOptionReservationMode,
                data_accompte: data_accompte,
                selectedOptionTarifMode: this.selectedOptionTarifMode,
                tarifs: this.isOn && !this.isSimpleTicketCircuit && !this.isEventCircuit ? this.tarifs : [],
                isTarifByDayHours: this.isTarifByDayHours && !this.isSimpleTicketCircuit && !this.isEventCircuit,
                configuredTarifs:
                    this.isTarifByDayHours && !this.isSimpleTicketCircuit && !this.isEventCircuit ? this.configuredTarifs : [],
                selectExperience: this.localSelectExperience,
                selected_taux_tva: this.isSimpleTicketCircuit ? 0 : this.selected_taux_tva,
                price: this.isSimpleTicketCircuit ? 0 : submittedPrice,
                active: this.localPrestation?.active || false,
                min_ticket: this.min_ticket,
                max_ticket: this.max_ticket,
                // EVENT-mode schedule. handleStep5 ignores this payload entirely for non-event modes
                // (day/hour tarifs are owned by storePlanning — PLPRB-1084); the backend is the single
                // authority, so we send it unchanged.
                horaires_specifiques: this.computedHoraires,

                detected_type: primaryDetectedType,
                detection_score: primaryDetectedType ? 1.0 : null,
                detection_confidence: primaryDetectedType ? 'exact' : null,
                user_selections: userSelections,
            }
            if (this.localPrestation?.id) formData.id = this.localPrestation.id

            try {
                const object = await axios.post('/api/v3/experience/save_exit_step_two', formData)
                console.log('✅ Step5 - Response from backend:', {
                    type: object.data.data.type,
                    max_contributors: object.data.data.max_contributors,
                    min_ticket_sent: formData.min_ticket,
                    max_ticket_sent: formData.max_ticket,
                })
                const savedPrestation = {
                    ...object.data.data,
                    price: submittedPrice,
                    vat_rate: this.selected_taux_tva,
                }
                this.$store.dispatch('experience/setPrice', submittedPrice)
                this.$emit('update-prestation', savedPrestation)

                // Update initialDataString after saving
                this.initialDataString = this.getFormDataString()

                if (redirect) {
                    const baseUrl = `${window.location.protocol}//${window.location.host}`
                    window.location.href = `${baseUrl}/back-office/v3/experience`
                } else {
                    console.log('✅ Données enregistrées avec succès (sans redirection).')
                }
                return true // ✅ Success - allow navigation to next step
            } catch (err) {
                console.error('❌ Erreur lors de la sauvegarde :', err.response || err)

                // ✅ FIX: Handle 422 validation errors specifically
                if (err.response?.status === 422 && err.response?.data?.errors) {
                    // Populate errors object with validation errors
                    this.errors = err.response.data.errors
                    this.error = err.response.data.message || 'Erreur de validation'
                } else {
                    // Generic error message for 500 or other errors
                    this.error = err.response?.data?.message || this.$t('experience.errors.save')
                }

                return false // ✅ FIX: Block navigation on error
            } finally {
                this.loading = false
            }
        },
        groupConsecutiveTarifs(flatEntries) {
            if (!flatEntries.length) return []
            // Collapse exact duplicates (same dates + hours) BEFORE grouping, so a stray duplicate
            // row from the DB can never surface twice (e.g. legacy single-day rows over a range).
            const seenInput = new Set()
            const deduped = flatEntries.filter((e) => {
                const k = this.tarifKey(e)
                if (seenInput.has(k)) return false
                seenInput.add(k)
                return true
            })
            const sorted = [...deduped].sort((a, b) => a.dateStart.localeCompare(b.dateStart))
            const result = []
            let current = null
            for (const entry of sorted) {
                const raw = entry.selectedDays
                const entryDays = Array.isArray(raw)
                    ? raw.flatMap((d) => (typeof d === 'string' ? d.split(',').map((s) => s.trim()) : [d])).filter(Boolean)
                    : raw
                      ? [raw]
                      : []
                if (!current) {
                    current = { ...entry, selectedDays: [...entryDays] }
                    continue
                }
                const sameSettings =
                    current.timeStart === entry.timeStart &&
                    current.timeEnd === entry.timeEnd &&
                    String(current.price) === String(entry.price) &&
                    String(current.vatRate) === String(entry.vatRate) &&
                    String(current.authorize_mixing) === String(entry.authorize_mixing) &&
                    String(current.off_peak_time) === String(entry.off_peak_time)
                const diffDays = !!current.dateEnd
                    ? (new Date(entry.dateStart) - new Date(current.dateEnd)) / (1000 * 60 * 60 * 24)
                    : Infinity
                if (sameSettings && diffDays === 1) {
                    current.dateEnd = entry.dateEnd
                    entryDays.forEach((d) => {
                        if (d && !current.selectedDays.includes(d)) current.selectedDays.push(d)
                    })
                } else {
                    result.push(this.buildTarifDisplayFields(current))
                    current = { ...entry, selectedDays: [...entryDays] }
                }
            }
            if (current) result.push(this.buildTarifDisplayFields(current))
            // Final safety net: never surface two scenarios with the same dates + hours (e.g. duplicate rows from DB).
            const seen = new Set()
            return result.filter((t) => {
                const k = this.tarifKey(t)
                if (seen.has(k)) return false
                seen.add(k)
                return true
            })
        },

        buildTarifDisplayFields(tarif) {
            const from = this.$t('common.from')
            const to = this.$t('common.to_date')
            const labelStart = this.formatDateFr(tarif.dateStart)
            const labelEnd = this.formatDateFr(tarif.dateEnd)
            const hasEndDate = !!tarif.dateEnd
            return {
                ...tarif,
                label: !hasEndDate
                    ? `${from} ${labelStart}`
                    : tarif.dateStart === tarif.dateEnd
                      ? labelStart
                      : `${from} ${labelStart} ${to} ${labelEnd}`,
                hours: `${tarif.timeStart} - ${tarif.timeEnd}`,
                label_days: this.formatDays(tarif.selectedDays),
            }
        },

        getDaysBetween(startDate, endDate, lang) {
            const days = []
            let weekdays = []
            if (lang === 'fr') {
                // Sunday should be index 0
                weekdays = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']
            } else {
                weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
            }

            // No end date: return all weekdays
            if (!endDate) {
                return weekdays.slice(1).concat(weekdays[0]).join(', ')
            }

            let currentDate = new Date(startDate)
            const end = new Date(endDate)

            while (currentDate <= end) {
                days.push(weekdays[currentDate.getDay()])
                currentDate.setDate(currentDate.getDate() + 1)
            }

            // Remove duplicates (if same day appears multiple times)
            return [...new Set(days)].join(', ')
        },
        setOriginalData() {
            this.originalData = JSON.parse(
                JSON.stringify({
                    tarifs: this.tarifs,
                    configuredTarifs: this.configuredTarifs,
                    acompte: this.acompte,
                    selectedOptionTarifMode: this.selectedOptionTarifMode,
                    selectedManyOption: this.selectedManyOption,
                })
            )
            this.initialDataString = this.getFormDataString()
        },

        getFormDataString() {
            return JSON.stringify({
                selectedOptionTarifMode: this.selectedOptionTarifMode,
                min_ticket: this.min_ticket,
                max_ticket: this.max_ticket,
                price: this.price,
                selected_taux_tva: this.selected_taux_tva,
                isOn: this.isOn,
                isTarifByDayHours: this.isTarifByDayHours,
                isAccompte: this.isAccompte,
                tarifs: this.tarifs,
                configuredTarifs: this.configuredTarifs,
                acompte: this.acompte,
            })
        },

        hasChanges() {
            console.log('🔍 Step5 - Checking for unsaved changes...', this.initialDataString)
            console.log('🔍 Step5 - getFormDataString...', this.getFormDataString)
            if (!this.initialDataString) return false
            return this.initialDataString !== this.getFormDataString()
        },

        exit() {
            const baseUrl = `${window.location.protocol}//${window.location.host}`
            window.location.href = `${baseUrl}/back-office/v3/experience`
        },
        setOptionTva() {
            if (Array.isArray(this.country_vat_rates)) {
                this.option_tva = [
                    { label: this.$t('common.choose'), value: '' },
                    ...this.country_vat_rates.map((tva) => ({
                        label: `${tva.pourcentage}%`,
                        value: tva.pourcentage,
                    })),
                ]
            }
        },
        getState(field) {
            if (this.errors[field]) return 'error'

            if (field === 'min_ticket') {
                // Check if empty first
                if (this.min_ticket === null || this.min_ticket === undefined || this.min_ticket === '') {
                    return 'default'
                }
                const val = Number(this.min_ticket)
                const maxVal = Number(this.max_ticket)
                return !isNaN(val) && (val === -1 || val >= 1) && (maxVal <= 0 || val <= maxVal) ? 'valid' : 'error'
            }
            if (field === 'max_ticket') {
                // Check if empty first
                if (this.max_ticket === null || this.max_ticket === undefined || this.max_ticket === '') {
                    return 'default'
                }
                const val = Number(this.max_ticket)
                const minVal = Number(this.min_ticket)
                return !isNaN(val) && (val === -1 || (val >= 1 && val >= minVal)) ? 'valid' : 'error'
            }
            if (field === 'price') {
                // Check if empty first
                if (this.price === null || this.price === undefined || this.price === '') {
                    return 'default'
                }
                const val = Number(this.price)
                return !isNaN(val) && val > 0 ? 'valid' : 'default'
            }

            // For other fields, check if they have a value
            const value = this[field]
            if (value !== null && value !== undefined && value !== '') return 'valid'
            return 'default'
        },
        clearError(field) {
            if (this.errors[field]) {
                this.errors[field] = ''
            }
        },
        // Durées proposées pour un PALIER : la grille complète + « Personnalisée », sans exclusion
        // — cf. store/modules/experience.js.
        getTierDurationOptions() {
            return this.$store.getters['experience/getTierDurationOptions']()
        },
        getFilteredDurations(currentValue) {
            return this.$store.getters['experience/getFilteredDurations'](currentValue)
        },
        /** Bowling : sélectionne « prix par groupe » (le verrou d'affichage est bowlingPricingLocked). */
        lockBowlingPricingMode() {
            if (this.bowlingPricingLocked && this.selectedOptionTarifMode !== 'price per group') {
                this.selectedOptionTarifMode = 'price per group'
            }
        },
        autoSetPricingMode(circuits) {
            // Bowling : toujours par groupe, quel que soit le circuit détecté (verrou bowlingPricingLocked).
            if (this.bowlingPricingLocked) {
                this.lockBowlingPricingMode()
                return
            }

            // Only auto-select if exactly 1 circuit is detected
            if (!circuits || circuits.length !== 1) return

            const circuitType = circuits[0]
            const config = this.$store.getters['experience/getPrestationTypeConfig'](circuitType)

            if (!config) return

            // Set flag to indicate this is an automatic change
            this.isAutoChange = true

            // Auto-select pricing mode based on circuit configuration
            //
            // Mode LIBRE (bowling) : l'empreinte ne porte pas les clés tarifaires — le gérant
            // choisit. On ne pré-sélection « par personne » (défaut historique du préréglage)
            // QUE si rien n'est chargé, sinon on écraserait le mode enregistré en édition.
            const modeIsFree = !('price_per_person' in config) && !('price_per_group' in config)

            if (modeIsFree) {
                if (!this.selectedOptionTarifMode) {
                    console.log(`🔄 Step5 - Mode tarifaire libre (${circuitType}), défaut "price per person"`)
                    this.selectedOptionTarifMode = 'price per person'
                }
            } else if (config.price_per_person === 1 && config.price_per_group === 0) {
                if (this.selectedOptionTarifMode !== 'price per person') {
                    console.log(`🔄 Step5 - Auto-selecting "price per person" (circuit: ${circuitType})`)
                    this.selectedOptionTarifMode = 'price per person'
                }
            } else if (config.price_per_group === 1 && config.price_per_person === 0) {
                if (this.selectedOptionTarifMode !== 'price per group') {
                    console.log(`🔄 Step5 - Auto-selecting "price per group" (circuit: ${circuitType})`)
                    this.selectedOptionTarifMode = 'price per group'
                }
            }

            // Reset flag after a tick
            this.$nextTick(() => {
                this.isAutoChange = false
            })
        },
        /**
         * Adapt min/max ticket values when circuit type changes.
         * Handles stale values from a previous circuit (e.g. simpleTicket max=9999
         * still present when user switches to paintball).
         *
         * Rules:
         * | Circuit       | min_ticket       | max_ticket        |
         * |---------------|------------------|-------------------|
         * | vr            | show (min>=1)    | keep/show         |
         * | simpleTicket  | force 1 (hidden) | force 9999 (hidden)|
         * | booking       | force 1 (hidden) | keep/show         |
         * | paintball     | show (min>=1)    | show (reset 9999) |
         */
        adaptMinMaxForCircuit(circuitType) {
            const currentMin = Number(this.min_ticket)
            const currentMax = Number(this.max_ticket)

            console.log(
                `🔄 Step5 - adaptMinMaxForCircuit(${circuitType}) - current min=${this.min_ticket}, max=${this.max_ticket}`
            )

            // --- min_ticket ---
            if (['simpleTicket', 'booking'].includes(circuitType)) {
                // Hidden circuits → force to 1
                this.$store.dispatch('experience/setMinTicket', 1)
                console.log(`🔒 Step5 - min_ticket forced to 1 (hidden by ${circuitType})`)
            } else if (['vr', 'paintball', 'bowling'].includes(circuitType)) {
                // VR/Paintball/Bowling: visible, must be >= 1
                if (isNaN(currentMin) || currentMin < 1) {
                    this.$store.dispatch('experience/setMinTicket', 1)
                    console.log(`🔓 Step5 - min_ticket reset to 1 (${circuitType} requires >= 1)`)
                }
            }

            // --- max_ticket ---
            if (circuitType === 'simpleTicket') {
                // simpleTicket: hidden → force to 9999
                this.$store.dispatch('experience/setMaxTicket', 9999)
                console.log('🔒 Step5 - max_ticket forced to 9999 (hidden by simpleTicket)')
            } else {
                // vr, booking, paintball: visible → reset stale 9999 value
                if (currentMax === 9999) {
                    this.$store.dispatch('experience/setMaxTicket', null)
                    console.log(`🔓 Step5 - max_ticket reset from 9999 (stale simpleTicket value) for ${circuitType}`)
                }
            }
        },
        reservationMode(selectedOptionReservationMode) {
            return this.$store.getters['experience/userSelections']?.reservation_mode === selectedOptionReservationMode
        },
    },
    watch: {
        selectedOptionTarifMode(newValue, oldValue) {
            if (!newValue) return

            const isPricePerPerson = newValue === 'price per person' ? 1 : 0
            const isPricePerGroup = newValue === 'price per group' ? 1 : 0

            // Dispatch to store
            this.$store.dispatch('experience/setUserSelection', {
                key: 'price_per_person',
                value: isPricePerPerson,
            })
            this.$store.dispatch('experience/setUserSelection', {
                key: 'price_per_group',
                value: isPricePerGroup,
            })

            console.log('🔵 Step5 - pricing_mode changed:', newValue)

            // Track last changed field
            this.lastChangedPricingField = newValue
        },
        // ✅ Watch store price_per_person: when reset to null (reservation_mode changed),
        // reset the local selectedOptionTarifMode so the pricing selector clears
        'userSelections.price_per_person'(newVal) {
            if (newVal === null && this.selectedOptionTarifMode !== null) {
                console.log('🔄 Step5 - price_per_person reset to null (reservation mode changed), clearing pricing selection')
                this.selectedOptionTarifMode = null
            }
        },
        bowlingPricingLocked: {
            handler() {
                this.lockBowlingPricingMode()
            },
            immediate: true,
        },
        matchingCircuits: {
            handler(circuits) {
                if (circuits.length === 1) {
                    // Exactly one circuit detected - apply restrictions
                    const circuitType = circuits[0]
                    console.log(`🎯 Step5 - Single circuit match: ${circuitType}`)
                    this.autoSetPricingMode(circuits)

                    const config = this.$store.getters['experience/getPrestationTypeConfig'](circuitType)

                    if (config) {
                        // When Step 3 fields alone determine the circuit (e.g. free_access_all_day → simpleTicket),
                        // disable the non-matching pricing option so the user can't select it.
                        // Clés tarifaires ABSENTES de l'empreinte (bowling = mode libre) :
                        // l'option reste choisissable — `isPricingModeDisabled` ne verrouille pas.
                        if (this.isPricingModeDisabled) {
                            this.options_tarif_mode.forEach((option) => {
                                if (option.value === 'price per person') {
                                    this.$set(option, 'disabled', 'price_per_person' in config && config.price_per_person !== 1)
                                } else if (option.value === 'price per group') {
                                    this.$set(option, 'disabled', 'price_per_group' in config && config.price_per_group !== 1)
                                }
                            })
                        } else {
                            // Multiple circuits still possible with pricing as differentiator
                            // Keep both options enabled to avoid self-locking
                            this.options_tarif_mode.forEach((option) => {
                                this.$set(option, 'disabled', false)
                            })
                        }
                    }

                    // ✅ Adapt min/max ticket values based on new circuit type
                    // This handles circuit changes (e.g. simpleTicket → paintball) including
                    // when the user edits an experience and changes the circuit type.
                    this.$nextTick(() => {
                        this.adaptMinMaxForCircuit(circuitType)
                    })
                } else if (circuits.length > 1) {
                    // Multiple circuits detected - user can choose freely, re-enable all options
                    console.log(`🎯 Step5 - Multiple circuits match (${circuits.length}):`, circuits)
                    console.log('🔓 Step5 - Re-enabling all pricing options (multiple circuits detected)')
                    this.options_tarif_mode.forEach((option) => {
                        this.$set(option, 'disabled', false)
                    })
                } else {
                    // No circuits match — pricing may contradict Step 3 fields
                    const circuitsWithoutPricing = this.$store.getters['experience/getMatchingCircuitsWithoutPricing']
                    if (circuitsWithoutPricing.length === 1) {
                        // Step 3 fields uniquely identify a circuit (e.g. reservation_mode = one_schedule_for_everyone → event)
                        // but pricing doesn't match → auto-correct pricing from circuit config
                        console.log(
                            '🔧 Step5 - Pricing contradicts Step 3, auto-correcting from circuit:',
                            circuitsWithoutPricing[0]
                        )
                        this.autoSetPricingMode(circuitsWithoutPricing)
                        const config = this.$store.getters['experience/getPrestationTypeConfig'](circuitsWithoutPricing[0])
                        if (config) {
                            // Même tolérance que le verrou : clé tarifaire absente = mode libre.
                            this.options_tarif_mode.forEach((option) => {
                                if (option.value === 'price per person') {
                                    this.$set(option, 'disabled', 'price_per_person' in config && config.price_per_person !== 1)
                                } else if (option.value === 'price per group') {
                                    this.$set(option, 'disabled', 'price_per_group' in config && config.price_per_group !== 1)
                                }
                            })
                        }
                    } else {
                        // Truly ambiguous or no match — re-enable all options
                        this.options_tarif_mode.forEach((option) => {
                            this.$set(option, 'disabled', false)
                        })
                    }
                }
            },
            // ✅ FIX: immediate: true ensures autoSetPricingMode runs when Step5 mounts
            // and the circuit is already detected from Step 3 fields
            immediate: true,
        },
        'userSelections.reservation_mode'(newVal, oldVal) {
            if (oldVal && newVal !== oldVal) {
                console.log('🔄 Step5 - Reservation mode changed, clearing planning data')
                this.isTarifByDayHours = false
                this.configuredTarifs = []
                this.events = []
                this.showPlanningModal = false
                if (this.$refs.planningCalendar) {
                    const api = this.$refs.planningCalendar.$refs.fullCalendar?.getApi()
                    if (api) api.refetchEvents()
                }
            }
        },
        prestation: {
            handler(newVal) {
                if (newVal) {
                    this.localPrestation = newVal

                    // Populate from user_choices
                    const pricingMode = this.getUserChoiceValue('pricing_mode')
                    if (pricingMode) {
                        this.selectedOptionTarifMode = pricingMode.replace(/_/g, ' ')
                    }
                    // Un bowling enregistré « par personne » avant le 2026-09-15 s'affiche par groupe :
                    // c'est ce que le serveur enregistrera.
                    this.lockBowlingPricingMode()

                    const reservationMode = this.getUserChoiceValue('reservation_mode')
                    if (reservationMode) {
                        this.$store.dispatch('experience/setReservationMode', reservationMode)
                    }

                    const mixGroups = this.getUserChoiceValue('mix_groupe_multi_espace') // or 'multi_space_group_mix' key?

                    const prohibitMixing = this.getUserChoiceValue('prohibit_mixing_min_people')
                    const isProhibitMixing =
                        prohibitMixing == 1 || prohibitMixing == '1' || prohibitMixing == true || prohibitMixing == 'true'
                    const mixGroup = this.getUserChoiceValue('mix_groupe_multi_espace')
                    const isMixGroup = mixGroup == 1 || mixGroup == '1' || mixGroup == true || mixGroup == 'true'

                    if (isMixGroup || isProhibitMixing) {
                        // Disable "price per group"
                        const option = this.options_tarif_mode.find((o) => o.value === 'price per group')
                        if (option) {
                            this.$set(option, 'disabled', true)
                            // If currently selected, switch to person?
                            if (this.selectedOptionTarifMode === 'price per group') {
                                this.selectedOptionTarifMode = 'price per person'
                            }
                        }
                    } else if (!this.isPricingModeDisabled) {
                        // Only re-enable if circuit detection doesn't lock pricing
                        const option = this.options_tarif_mode.find((o) => o.value === 'price per group')
                        if (option) this.$set(option, 'disabled', false)
                    }

                    this.price = newVal.price != null && newVal.price !== '' ? parseFloat(newVal.price).toFixed(2) : ''
                    const vatRate = newVal.vat_rate
                    const hasPrice = newVal.price != null && newVal.price !== '' && parseFloat(newVal.price) >= 0
                    if (vatRate != null && vatRate !== '' && (Number(vatRate) !== 0 || hasPrice)) {
                        // Pas de toFixed(1) : il ramenait 9,975 % à 10 % avant l'envoi
                        // à /api/v3/experience/{id}/sync-price. Un taux se transmet tel quel.
                        this.selected_taux_tva = parseFloat(vatRate)
                    } else {
                        this.selected_taux_tva = ''
                    }
                    this.isAccompte = newVal.deposite == null ? false : newVal.deposite
                    if (this.isAccompte) {
                        this.acompte = {
                            type: newVal.accompte_type,
                            value: newVal.accompte,
                        }
                    } else {
                        this.acompte = {
                            type: null,
                            value: null,
                        }
                    }

                    const dbType = Number(newVal.type)

                    if (!isNaN(dbType) && dbType > 0) {
                        this.$store.dispatch('experience/setMinTicket', dbType)
                    } else {
                        // DB is -1 (legacy default) or 0 → check prohibit_mixing from user_choices
                        const prohibitMixingForMinTicket = this.getUserChoiceValue('prohibit_mixing_min_people')
                        const isProhibitMixingForMinTicket =
                            prohibitMixingForMinTicket == 1 ||
                            prohibitMixingForMinTicket == '1' ||
                            prohibitMixingForMinTicket == true ||
                            prohibitMixingForMinTicket == 'true'
                        const prohibitMixingValueForMinTicket = this.getUserChoiceValue('prohibited_mixing_min_people_value')

                        if (
                            isProhibitMixingForMinTicket &&
                            prohibitMixingValueForMinTicket !== null &&
                            parseInt(prohibitMixingValueForMinTicket) > 0
                        ) {
                            const minFromMixing = parseInt(prohibitMixingValueForMinTicket)
                            this.$store.dispatch('experience/setMinTicket', minFromMixing)
                        } else {
                            this.$store.dispatch('experience/setMinTicket', 1)
                        }
                    }

                    if (
                        newVal.max_contributors !== undefined &&
                        newVal.max_contributors !== null &&
                        newVal.max_contributors !== 0
                    ) {
                        this.$store.dispatch('experience/setMaxTicket', newVal.max_contributors)
                        console.log('✅ Step5 - Restored max_ticket to:', newVal.max_contributors)
                    } else {
                        // Ensure it's -1 by default
                        this.$store.dispatch('experience/setMaxTicket', -1)
                    }

                    // Sync dynamicDurationBlocks with backend prices_by_times
                    const storeBlocks = this.$store.getters['experience/dynamicDurationBlocks'] || []

                    if (Array.isArray(newVal.prices_by_times) && newVal.prices_by_times.length > 0) {
                        const blocks = newVal.prices_by_times.map((item) => ({
                            _uid: item._uid || Date.now() + '_' + Math.random().toString(36).substr(2, 9),
                            id: item.id,
                            value: item.duration || '',
                            price: item.price != null ? item.price : '',
                            personilize: null,
                            name: item.price_duration_description || newVal.name || '',
                            prices_degressif: item.lot_prices_duration
                                ? item.lot_prices_duration.map((lot) => ({
                                      persons: lot.nb_contributor,
                                      // Historiquement la durée du palier n'était qu'une recopie de
                                      // celle de l'option : une valeur identique signifie donc
                                      // « pas de surcharge » et doit s'afficher vide. Une durée hors
                                      // grille se présente comme « Personnalisée » (-1) + sa valeur.
                                      ...this.$store.getters['experience/getTierDurationFields'](lot),
                                      // 0 = « non renseigné » (écrit par l'étape Disponibilités, ignoré par
                                      // le résolveur) : il se présente vide, à saisir (Prix* > 0).
                                      price: Number(lot.price) > 0 ? parseFloat(lot.price).toFixed(2) : '',
                                      editing: false,
                                  }))
                                : [],
                        }))

                        // Toujours refléter la dernière version renvoyée par l'API
                        this.$store.dispatch('experience/setDynamicDurationBlocks', blocks)
                    } else if (!storeBlocks.length) {
                        const availableOptions = this.getFilteredDurations('')
                        const defaultOption = availableOptions.find((o) => o.value !== -1)
                        const defaultDuree = defaultOption ? defaultOption.value : ''

                        // Pas de prices_by_times et store vide : initialiser un bloc par défaut
                        this.$store.dispatch('experience/setDynamicDurationBlocks', [
                            {
                                _uid: 'default_' + (newVal.id || Date.now()),
                                id: null,
                                value: newVal.duration || defaultDuree,
                                price: '',
                                personilize: null,
                                name: newVal.name || '',
                                prices_degressif: [],
                            },
                        ])
                    }
                    if (this.hasConfiguredOptions()) this.isOn = true
                    // Only restore specific pricing (by day/hour) when the current circuit supports it.
                    // For Event/SimpleTicket, these features are hidden — reset them to avoid stale data
                    // when the user later switches to a circuit that does support them.
                    if (newVal.prestation_planning && newVal.prestation_planning.length) {
                        if (!this.isEventCircuit && !this.isSimpleTicketCircuit) {
                            this.isTarifByDayHours = true
                            this.configuredTarifs = this.groupConsecutiveTarifs(
                                newVal.prestation_planning.flatMap((item) => {
                                    const endDate = item.end_date && item.end_date !== SENTINEL_END_DATE ? item.end_date : null
                                    return (item.planning_prices || []).map((planningPrice) => ({
                                        dateStart: item.start_date,
                                        dateEnd: endDate,
                                        timeStart: (planningPrice['start_time'] || '').substring(0, 5),
                                        timeEnd: (planningPrice['end_time'] || '').substring(0, 5),
                                        price: parseFloat(planningPrice['price']).toFixed(2),
                                        vatRate: planningPrice['vat_rate'],
                                        authorize_mixing: planningPrice['authorize_mixing'],
                                        off_peak_time: planningPrice['off_peak_time'],
                                        selectedDays: this.getDaysBetween(item.start_date, endDate, 'en').split(', '),
                                        planningId: item.id,
                                    }))
                                })
                            )
                        } else {
                            this.isTarifByDayHours = false
                            this.configuredTarifs = []
                        }
                    }

                    // Mapping horaires_specifiques (One schedule for everyone) — Event only
                    // For non-Event circuits, clear stale events data to prevent old Event
                    // schedule from appearing in other experience types.
                    if (this.isEventCircuit && newVal.prestation_planning && newVal.prestation_planning.length) {
                        this.events = newVal.prestation_planning.flatMap((item) => {
                            const endDate = item.end_date && item.end_date !== SENTINEL_END_DATE ? item.end_date : null
                            return (item.planning_prices || []).map((planningPrice) => {
                                // Normalize legacy "00:00"/"00:00" sentinel (= no lunch break) to null
                                // so backend validation does not flag lunchEnd as not-after lunchStart.
                                const rawLunchStart = planningPrice?.lunch_start
                                const rawLunchEnd = planningPrice?.lunch_end
                                const hasLunch =
                                    rawLunchStart &&
                                    rawLunchEnd &&
                                    !(rawLunchStart === '00:00' && rawLunchEnd === '00:00') &&
                                    !(rawLunchStart === '00:00:00' && rawLunchEnd === '00:00:00')
                                return {
                                    dateStart: item.start_date,
                                    dateEnd: endDate,
                                    timeStart: planningPrice?.start_time,
                                    timeEnd: planningPrice?.end_time,
                                    lunchStart: hasLunch ? rawLunchStart : null,
                                    lunchEnd: hasLunch ? rawLunchEnd : null,
                                    isPeack: !!(planningPrice?.lunch_start && planningPrice?.lunch_end),
                                    label: endDate
                                        ? this.formatDateFr(item.start_date) + ' ' + this.formatDateFr(endDate)
                                        : this.formatDateFr(item.start_date),
                                    hours:
                                        (planningPrice?.start_time || '').split(':')[0] +
                                        'h – ' +
                                        (planningPrice?.end_time || '').split(':')[0] +
                                        'h',
                                    label_days: this.getDaysBetween(item.start_date, endDate, 'fr'),
                                    selectedDays: this.getDaysBetween(item.start_date, endDate, 'en').split(', '),
                                }
                            })
                        })
                    } else if (!this.isEventCircuit) {
                        this.events = []
                    }
                    this.$nextTick(() => {
                        this.setOriginalData()
                    })
                }
            },
            immediate: true,
            deep: true,
        },
        country_vat_rates: {
            handler() {
                this.setOptionTva()
            },
            deep: true,
            immediate: true,
        },
        price(newValue) {
            this.$store.dispatch('experience/setPrice', newValue)

            // Sync price → 1st tarif row
            if (this.isOn && this.tarifs.length > 0 && !this._syncingFromTarif) {
                const parsed = parseFloat(newValue)
                const tarifPrice = parseFloat(this.tarifs[0].price)
                if (!isNaN(parsed) && parsed !== tarifPrice) {
                    this._syncingFromPrice = true
                    const updated = [...this.tarifs]
                    updated[0] = { ...updated[0], price: parsed.toFixed(2) }
                    this.tarifs = updated
                    this.$nextTick(() => {
                        this._syncingFromPrice = false
                    })
                }
            }

            // Instant client-side update for calendar default-price events
            if (this.isTarifByDayHours) {
                this.syncCalendarDefaultPrices(newValue)
            }

            this.$nextTick(() => {
                this.setOriginalData()
            })
        },
        // Sync 1st tarif row price → main price
        tarifs: {
            handler(newTarifs) {
                // Do not let an old duration tariff overwrite the main prestation
                // price while the form is being hydrated. Only a focused tariff
                // edit is allowed to sync back to the main price.
                if (this.isOn && newTarifs.length > 0 && !this._syncingFromPrice && this.activeField === 'tarif') {
                    const tarifPrice = parseFloat(newTarifs[0].price)
                    const currentPrice = parseFloat(this.price)
                    if (!isNaN(tarifPrice) && tarifPrice !== currentPrice) {
                        this._syncingFromTarif = true
                        this.price = tarifPrice.toFixed(2)
                        this.$nextTick(() => {
                            this._syncingFromTarif = false
                        })
                    }
                }
            },
            deep: true,
        },
        isAccompte(newValue) {
            if (newValue && (!this.acompte.value || this.acompte.value === 0)) {
                // Reset to null if enabling and value is empty/default
                this.acompte = { type: null, value: null }
            }
            this.$store.dispatch('experience/setDataAccompte', {
                isAccompte: newValue,
                acompte: this.acompte,
            })
        },
        acompte: {
            handler(newValue) {
                this.$store.dispatch('experience/setDataAccompte', {
                    isAccompte: this.isAccompte,
                    acompte: newValue,
                })
            },
            deep: true,
        },
        // When circuit type changes to paintball/booking/vr, enforce min_ticket >= 1
        isPaintballBookingOrVRCircuit(newVal) {
            if (newVal && (this.min_ticket === null || this.min_ticket === -1 || Number(this.min_ticket) < 1)) {
                this.$store.dispatch('experience/setMinTicket', 1)
            }
        },
        // Force min_ticket to 1 when hidden, reset to editable default when shown again
        showMinTicket(visible, wasVisible) {
            if (!visible) {
                // Hidden → force value to 1
                this.$store.dispatch('experience/setMinTicket', 1)
                console.log('🔒 Step5 - min_ticket hidden, forced to 1')
            } else if (wasVisible === false) {
                // Was hidden, now visible → reset to sensible default
                const currentVal = Number(this.min_ticket)
                if (this.isPaintballBookingOrVRCircuit) {
                    // paintball/booking/vr: min >= 1, keep 1 as it's valid
                    if (currentVal < 1) {
                        this.$store.dispatch('experience/setMinTicket', 1)
                    }
                }
                console.log('🔓 Step5 - min_ticket now visible, current value:', this.min_ticket)
            }
        },
        // Force max_ticket to 9999 when hidden, reset when shown again
        showMaxTicket(visible, wasVisible) {
            if (!visible) {
                // Hidden → force value to 9999
                this.$store.dispatch('experience/setMaxTicket', 9999)
                console.log('🔒 Step5 - max_ticket hidden, forced to 9999')
            } else if (wasVisible === false) {
                // Was hidden, now visible → reset forced 9999 to empty so user must fill it
                const currentVal = Number(this.max_ticket)
                if (currentVal === 9999) {
                    this.$store.dispatch('experience/setMaxTicket', null)
                    console.log('🔓 Step5 - max_ticket now visible, reset from 9999 to empty (user must set)')
                } else {
                    console.log('🔓 Step5 - max_ticket now visible, keeping value:', this.max_ticket)
                }
            }
        },
    },
    computed: {
        planningFetchUrl() {
            if (!this.localPrestation?.id) return null
            return `/api/v3/experience/planning/${this.localPrestation.id}/1/filter`
        },
        computedHoraires() {
            // Detect event mode by the reservation_mode user-choice KEY (not user_choices[0], whose order
            // is not guaranteed) — keeps front↔backend event detection consistent (PLPRB-1084 review).
            return this.getUserChoiceValue('reservation_mode') === 'one_schedule_for_everyone'
                ? this.events
                : this.horaires_specifiques
        },
        localSelectExperience() {
            return this.selected_experience || {}
        },
        hasId() {
            return this.id !== null && this.id !== undefined
        },
        min_ticket: {
            get() {
                return this.$store.getters['experience/min_ticket']
            },
            set(val) {
                this.$store.dispatch('experience/setMinTicket', val)
            },
        },
        max_ticket: {
            get() {
                return this.$store.getters['experience/max_ticket']
            },
            set(val) {
                this.$store.dispatch('experience/setMaxTicket', val)
            },
        },
        currentCircuit() {
            return this.$store.getters['experience/currentCircuit']
        },
        matchingCircuits() {
            return this.$store.getters['experience/getMatchingCircuits']
        },
        // Check if current circuit is simpleTicket
        isSimpleTicketCircuit() {
            return this.matchingCircuits.length === 1 && this.matchingCircuits[0] === 'simpleTicket'
        },
        // Check if current circuit is VR
        isVRCircuit() {
            return this.matchingCircuits.length === 1 && this.matchingCircuits[0] === 'vr'
        },
        // Check if current circuit is booking
        isBookingCircuit() {
            return this.matchingCircuits.length === 1 && this.matchingCircuits[0] === 'booking'
        },
        // Check if current circuit is event
        isEventCircuit() {
            return this.matchingCircuits.length === 1 && this.matchingCircuits[0] === 'event'
        },
        // Check if current circuit is paintball
        isPaintballCircuit() {
            return this.matchingCircuits.length === 1 && this.matchingCircuits[0] === 'paintball'
        },
        // Check if current circuit requires min_ticket >= 1 (paintball/booking)
        isPaintballOrBookingCircuit() {
            return this.matchingCircuits.length === 1 && ['paintball', 'booking'].includes(this.matchingCircuits[0])
        },
        // Check if current circuit requires min_ticket >= 1 (paintball/booking/vr)
        isPaintballBookingOrVRCircuit() {
            // bowling y figure aussi : côté billets il se comporte comme la VR (min visible, >= 1).
            return (
                this.matchingCircuits.length === 1 && ['paintball', 'booking', 'vr', 'bowling'].includes(this.matchingCircuits[0])
            )
        },
        /**
         * Visibility rules for min/max ticket fields based on circuit type:
         * | Circuit       | Show min | Show max | Force min | Force max |
         * |---------------|----------|----------|-----------|-----------|
         * | vr            | show     | show     | -         | -         |
         * | simpleTicket   | hide     | hide     | 1         | 9999      |
         * | booking       | hide     | show     | 1         | -         |
         * | paintball     | show     | show     | -         | -         |
         */
        showMinTicket() {
            // VR and paintball show the min field; simpleTicket, booking hide it
            if (this.isSimpleTicketCircuit || this.isBookingCircuit) return false
            return true
        },
        showMaxTicket() {
            // Only simpleTicket hides the max field
            if (this.isSimpleTicketCircuit) return false
            return true
        },
        // Min value for the min_ticket input: 1 for paintball/booking/vr, -1 for others
        minTicketMinValue() {
            return 1
        },
        userSelections() {
            return this.$store.getters['experience/userSelections']
        },
        // Cf. Step4 : seule une expérience à « durée dynamique » (un bowling) a des paliers de durée.
        hasDynamicDuration() {
            return Boolean(this.$store.getters['experience/userSelections']?.dynamic_duration_enabled)
        },
        /**
         * « Durée dynamique » cochée : le prix est TOUJOURS par groupe et la question est masquée
         * (décision 2026-09-15 ; le serveur l'impose aussi). Le drapeau vient du store (étape 3) ou,
         * si l'étape 3 n'a encore rien chargé, du choix enregistré sur la prestation. Décocher la case
         * déverrouille : le mode reste sur « par groupe » jusqu'à ce que l'admin le change.
         */
        bowlingPricingLocked() {
            if (this.hasDynamicDuration) return true
            const stored = this.$store.getters['experience/userSelections']?.dynamic_duration_enabled
            if (stored !== null && stored !== undefined) return false
            const saved = this.getUserChoiceValue('dynamic_duration_enabled')
            return saved === 1 || saved === '1' || saved === true || saved === 'true'
        },
        /**
         * « Tarifs spécifiques sur les paliers de durée » : sur un bowling, les tarifs spécifiques ne
         * sont possibles que si le prix par personne est identique sur la base, les options et tous
         * les paliers. Réévalué à chaque saisie de prix. Confort d'interface : le serveur décide
         * (storePlanning, Step5Request). Les autres circuits ne sont pas concernés.
         * Section « options et tarifs » éteinte : aucun prix n'est affiché, donc rien à juger ici —
         * on suit la décision du serveur sur les paliers en base (`specific_pricing_allowed`, posé par
         * le chargeur et par la sauvegarde). Absent = autorisé, comme avant.
         */
        specificPricingEligible() {
            if (!this.hasDynamicDuration || this.isSimpleTicketCircuit || this.isEventCircuit) return true
            if (!this.isOn) return this.localPrestation?.specific_pricing_allowed !== false
            return uniformPricePerPerson(this.tarifs, this.selectedOptionTarifMode === 'price per group', this.price) !== null
        },
        /** Refus serveur (422) sur le switch des tarifs spécifiques, s'il y en a un. */
        specificPricingServerError() {
            const messages = this.errors?.isTarifByDayHours
            return Array.isArray(messages) ? messages[0] : messages || ''
        },
        isPricingModeDisabled() {
            if (this.bowlingPricingLocked) return true
            // Disable pricing ONLY when Step 3 fields alone (without pricing)
            // already narrow to exactly 1 circuit.
            // e.g. multi_space_group_mix=1 → only VR → pricing is a consequence, lock it.
            // `bowling` se déclare par sa propre case (dynamic_duration_enabled), qui est une clé
            // d'étape 3 : l'étape 3 seule suffit donc à trancher entre `booking` et `bowling`.
            // MAIS son mode tarifaire est LIBRE (empreinte sans clés tarifaires, comme côté
            // serveur) : le verrou ne s'applique qu'aux circuits dont l'empreinte épingle le mode.
            const withoutPricing = this.$store.getters['experience/getMatchingCircuitsWithoutPricing']
            if (withoutPricing.length !== 1) {
                return false
            }
            const config = this.$store.getters['experience/getPrestationTypeConfig'](withoutPricing[0])
            if (config && !('price_per_person' in config) && !('price_per_group' in config)) {
                return false
            }
            return true
        },
        // Check if prestation is published (not a draft)
        isPublishedPrestation() {
            return this.prestation && !this.prestation.draft
        },
        pricingModeTooltip() {
            if (this.isPublishedPrestation) {
                return this.$t('experience.step5.pricingModeTooltipPublished')
            }
            if (this.bowlingPricingLocked) {
                return this.$t('experience.step5.pricingModeLockedByBowling')
            }
            if (this.isPricingModeDisabled && this.matchingCircuits.length === 1) {
                const circuitType = this.matchingCircuits[0]
                return this.$t('experience.step5.pricingModeLockedByCircuit', { circuit: circuitType })
            }
            return ''
        },
        tarifs: {
            get() {
                // Transform dynamicDurationBlocks to tarifs format
                const blocks = this.$store.getters['experience/dynamicDurationBlocks']
                if (!blocks || blocks.length === 0) {
                    return [
                        {
                            _uid: 'default_' + (this.prestation?.id || 'new'),
                            name: this.prestation?.name || '',
                            duree: this.prestation?.duration || '',
                            price: '',
                            editing: false,
                            prices_degressif: [],
                        },
                    ]
                }

                return blocks.map((block) => {
                    const parsedPrice = parseFloat(block.price)
                    return {
                        _uid: block._uid,
                        id: block.id || null,
                        name: block.name || this.prestation?.name || '',
                        duree: block.value === -1 ? block.personilize : block.value,
                        price: !isNaN(parsedPrice) ? parsedPrice.toFixed(2) : '',
                        editing: false,
                        prices_degressif: block.prices_degressif || [],
                    }
                })
            },
            set(tarifs) {
                // ✅ FIX BUG #5: Transform tarifs back to dynamicDurationBlocks format while preserving custom durations
                const currentBlocks = this.$store.getters['experience/dynamicDurationBlocks'] || []

                const blocks = tarifs.map((tarif, index) => {
                    const parsedPrice = parseFloat(tarif.price)
                    const currentBlock = currentBlocks[index]

                    // Check if this duration is a custom value
                    // Custom durations are stored as value: -1, personilize: <actual_value>
                    const isCustomDuration = currentBlock && currentBlock.value === -1 && currentBlock.personilize === tarif.duree

                    return {
                        _uid: tarif._uid || null,
                        id: tarif.id || null,
                        value: isCustomDuration ? -1 : tarif.duree,
                        price: !isNaN(parsedPrice) ? parsedPrice.toFixed(2) : '',
                        personilize: isCustomDuration ? tarif.duree : null, // ✅ Preserve custom duration
                        name: tarif.name || '',
                        prices_degressif: tarif.prices_degressif || [],
                    }
                })

                this.$store.dispatch('experience/setDynamicDurationBlocks', blocks)
            },
        },
        selectedOptionReservationMode() {
            return this.$store.getters['experience/reservationMode']
        },
        // 🔥 DÉTECTION AVEC CircuitDetectionService
        detectionInfo() {
            // Use CircuitDetectionService for real-time detection
            const userSelections = {
                reservation_mode: this.$store.getters['experience/reservationMode'],
                selectedOptionTarifMode: this.$store.getters['experience/selectedOptionTarifMode'],
                isActiveProhibitMixingMinPeople: this.$store.getters['experience/isActiveProhibitMixingMinPeople'],
                prohibitMixingMinPeople: this.$store.getters['experience/prohibitMixingMinPeople'],
                dynamic_duration_enabled: this.$store.getters['experience/userSelections']?.dynamic_duration_enabled ?? null,
            }

            const detection = circuitDetectionService.getBestDetection(userSelections)

            if (!detection.success) {
                return {
                    type: null,
                    score: 0,
                    confidence: CONFIDENCE_LEVELS.LOW,
                    isAmbiguous: false,
                }
            }

            return {
                type: detection.type,
                score: detection.score / 100,
                confidence: detection.confidence,
                isAmbiguous: detection.isAmbiguous,
                allScores: detection.allScores,
            }
        },
        detectedType() {
            const info = this.detectionInfo
            return info.confidence === CONFIDENCE_LEVELS.EXACT || info.confidence === CONFIDENCE_LEVELS.HIGH ? info.type : null
        },
        // ✅ Control visibility of detected type block from store
        showDetectedType() {
            return this.$store.getters['experience/showDetectedType']
        },
        detectionMessage() {
            const info = this.detectionInfo
            if (!info.type) {
                return this.$t('experience.step3.detection.waitingConfig')
            }

            const typeName = circuitDetectionService.getTypeName(info.type)

            if (info.isAmbiguous) {
                return this.$t('experience.step3.detection.detectedTypeAmbiguous', { typeName })
            }

            return this.$t('experience.step3.detection.detectedType', { typeName })
        },
        hasDetectionAmbiguity() {
            return this.detectionInfo.isAmbiguous || false
        },
    },
    mounted() {
        // ✅ FIX: Initialize min_ticket and max_ticket from prestation on mount
        if (this.prestation && this.prestation.user_choices) {
            // DB value takes priority over prohibit_mixing
            const dbTypeMounted = Number(this.prestation.type)
            console.log('🔍 Step5 mounted - DB type:', this.prestation.type, '→', dbTypeMounted)

            if (!isNaN(dbTypeMounted) && dbTypeMounted > 0) {
                // DB has a real value → always use it
                this.$store.dispatch('experience/setMinTicket', dbTypeMounted)
                console.log('✅ Step5 mounted - min_ticket from DB:', dbTypeMounted)
            } else {
                // DB is -1 or 0 → check prohibit_mixing from user_choices
                const prohibitMixingMounted = this.getUserChoiceValue('prohibit_mixing_min_people')
                const isProhibitMixingMounted =
                    prohibitMixingMounted == 1 ||
                    prohibitMixingMounted == '1' ||
                    prohibitMixingMounted == true ||
                    prohibitMixingMounted == 'true'
                const prohibitMixingValueMounted = this.getUserChoiceValue('prohibited_mixing_min_people_value')

                if (isProhibitMixingMounted && prohibitMixingValueMounted !== null && parseInt(prohibitMixingValueMounted) > 0) {
                    const minFromMixing = parseInt(prohibitMixingValueMounted)
                    this.$store.dispatch('experience/setMinTicket', minFromMixing)
                    console.log('✅ Step5 mounted - min_ticket from prohibit_mixing:', minFromMixing)
                } else {
                    const defaultMin = 1
                    this.$store.dispatch('experience/setMinTicket', defaultMin)
                    console.log('✅ Step5 mounted - Defaulted min_ticket to', defaultMin)
                }
            }
            if (
                this.prestation.max_contributors !== undefined &&
                this.prestation.max_contributors !== null &&
                this.prestation.max_contributors !== 0
            ) {
                this.$store.dispatch('experience/setMaxTicket', this.prestation.max_contributors)
                console.log('✅ Step5 mounted - Initialized max_ticket:', this.prestation.max_contributors)
            } else {
                if (!this.max_ticket && this.max_ticket !== -1) {
                    this.$store.dispatch('experience/setMaxTicket', -1)
                }
            }
        } else {
            // New experience — only default to -1 if store is truly empty
            const currentMinTicket = this.$store.getters['experience/min_ticket']
            if (currentMinTicket === null || currentMinTicket === undefined) {
                this.$store.dispatch('experience/setMinTicket', 1)
                console.log('✅ Step5 mounted (new) - Defaulted min_ticket to 1')
            }
            if (!this.max_ticket && this.max_ticket !== -1) {
                this.$store.dispatch('experience/setMaxTicket', -1)
                console.log('✅ Step5 mounted (new) - Defaulted max_ticket to -1')
            }
        }

        // Enforce forced values based on circuit type after initial data is loaded
        this.$nextTick(() => {
            if (this.matchingCircuits.length === 1) {
                this.adaptMinMaxForCircuit(this.matchingCircuits[0])
            }
        })
    },
    beforeDestroy() {
        EventBus.$off('dt-selected-columns-changed', this.handleSelectedColumns)
    },
}
</script>
<style scoped>
/* Partie gauche */
.form-section {
    flex: 1;
}

.gestion_creneau {
    background-color: #1a1f370d;
    padding: 10px 12px;
}

.dark .gestion_creneau {
    background-color: rgba(255, 255, 255, 0.05);
}

/* + Ajouter une option button */
.add-option-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 14px;
    border: 1.5px dashed #d0d0d0;
    border-radius: 16px;
    background-color: transparent;
    color: #666;
    cursor: pointer;
    font-size: 13px;
    transition: all 0.2s ease;
    margin-top: 6px;
}
.add-option-btn:hover {
    background-color: #fafafa;
    border-color: #999;
    color: #444;
}
.dark .add-option-btn {
    border-color: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.5);
}
.dark .add-option-btn:hover {
    background-color: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.3);
    color: rgba(255, 255, 255, 0.7);
}
</style>
